import { Language } from '@coolblue-development/constants';

import type { VacancyDetailPageHeaderBlock } from '../VacancyDetailPage';
import type { VacancyGroupDetailPageHeaderBlock } from '../VacancyGroupDetailPage';
import {
  GroupApplicationFormBlock,
  ImageGalleryBlock,
  LocationSpecificApplicationFormBlock,
  ProcedureExplainerBlock,
  RecruiterBlock,
  TextBlock
} from './Blocks';
import { Hours } from './Hours';
import { SalaryRange, SalaryValue } from './Salary';
import { SEO } from './SEO';

export enum PageType {
  VacancyDetail = 'VACANCY_DETAIL',
  VacancyGroupDetail = 'VACANCY_GROUP_DETAIL'
}

export type Page = {
  id: string;
  datePosted: string;
  validThrough: string | null;
  subsidiary: string;
  language: string;
  slug: string;
  title: string;
  seo: SEO;
  status: Status;
  otherLanguages: OtherLanguages[];
};

type SharedBlocks = ImageGalleryBlock | TextBlock | RecruiterBlock | ProcedureExplainerBlock;

export type FollowUpFlow = (typeof FollowUpFlows)[keyof typeof FollowUpFlows];

export const FollowUpFlows = {
  NONE: 'None',
  SPOTLER: 'Spotler'
} as const;

export type ATSSource = (typeof ATSSources)[keyof typeof ATSSources];
export const ATSSources = {
  SMART_RECRUITERS_JOB: 'SmartRecruiterJob',
  SMART_RECRUITERS_POSTING: 'SmartRecruiterPosting'
} as const;

export type ATSData = { id: string; reference?: string | null; source: ATSSource };

export type Vacancy = {
  id: string;
  location: {
    cityName: string;
    displayName: string;
  };
  hours?: Hours;
  salaryValue?: SalaryValue;
  salaryRange?: SalaryRange;
  relocationPossible: boolean;
  followUpFlow: FollowUpFlow;
  atsData?: ATSData;
};

export type VacancyDetailPageBlock =
  | VacancyDetailPageHeaderBlock
  | LocationSpecificApplicationFormBlock
  | SharedBlocks;

export type VacancyDetailPage = {
  type: PageType.VacancyDetail;
  vacancyId: string;
  vacancy: Vacancy;
  blocks: VacancyDetailPageBlock[];
} & Page;

export type VacancyGroupDetailPageBlock =
  | VacancyGroupDetailPageHeaderBlock
  | GroupApplicationFormBlock
  | SharedBlocks;

type VacancyGroup = {
  id: string;
  locations: { cityName: string; displayName: string }[];
  salaryRange?: SalaryRange;
  hours?: Hours;
  followUpFlow: FollowUpFlow;
};

export type VacancyGroupDetailPage = {
  type: PageType.VacancyGroupDetail;
  vacancyGroupId: string;
  vacancyGroup: VacancyGroup;
  blocks: VacancyGroupDetailPageBlock[];
} & Page;

export type Status = (typeof Statuses)[keyof typeof Statuses];

export const Statuses = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED'
} as const;

export type OtherLanguages = {
  slug: string | undefined;
  language: Language;
};
